import {
    forwardRef,
    useImperativeHandle,
    useState,
} from 'react'

const SSN_TOOLTIP = "Your security is our priority. Your data is encrypted and stored and is never shared with third parties for marketing."

const USER_TYPES = [
    {
        id: 'us_citizen',
        label: 'A Citizen of the United States',
        documentOptions: [
            {
                id: 'passport',
                label: 'US Passport or US Passport Card',
                condition: 'Option 1 (One Step)',
                documentForBoth: 'US Passport or US Passport Card',
            },
            {
                id: 'dl_two_steps',
                condition: 'Option 2 (Two Steps)',
                label: "US State-issued Driver's License / ID AND one of the following:",
                documentForID: "US State-issued Driver's License / ID",
                subOptions: [
                    { id: 'dl_ssn_card', label: 'Unrestricted "Social Security Card"', ssnTooltip: true },
                    { id: 'dl_birth_cert', label: 'Original or Certified Birth Certificate' },
                    { id: 'dl_naturalization', label: 'Certificate of Naturalization (Form N-550 or N-570)' },
                ],
            },
        ],
    },
    {
        id: 'lawful_permanent_resident',
        label: 'Lawful Permanent Resident (Green Card Holder)',
        documentOptions: [
            {
                id: 'i551',
                label: 'Permanent Resident Card (Form I-551)',
                condition: 'Option 1',
                documentForBoth: 'Permanent Resident Card (Form I-551)',
            },
            {
                id: 'foreign_passport_i551_stamp',
                label: 'Foreign Passport with a temporary I-551 stamp (ADIT Stamp)',
                condition: 'Option 2',
                documentForID: "US State-issued Driver's License / ID OR Foreign Passport",
                documentForAuth: 'US Visa page containing the I-551 stamp.',
            },
            {
                id: 'i551_expired_i797',
                label: 'Expired Permanent Resident Card with Extension',
                condition: 'Option 3',
                documentForID: "US State-issued Driver's License / ID OR Foreign Passport",
                documentForAuth: 'Expired Permanent Resident Card (Form I-551) AND the official I-797 Extension Notice.',
            },
        ],
    },
    {
        id: 'ead_holder',
        label: 'Employment Authorization Document (EAD) Holder (Includes F-1 OPT/STEM OPT)',
        documentOptions: [
            {
                id: 'ead_i766',
                label: 'Employment Authorization Document (Form I-766)',
                condition: 'Option 1',
                documentForBoth: 'Employment Authorization Document (Form I-766)',
            },
            {
                id: 'ead_expired_i797c',
                label: 'Expired Employment Authorization Document with Extension',
                condition: 'Option 2',
                documentForID: "US State-issued Driver's License / ID OR Foreign Passport",
                documentForAuth: 'Expired EAD Card (Form I-766) AND the I-797C Receipt Notice (the letter from USCIS confirming your renewal application was timely filed)',
            },
        ],
    },
    {
        id: 'non_immigrant_visa',
        label: 'Non-Immigrant Visa Holder (H-1B, L-1, TN, O-1, etc.)',
        note: 'Please have your latest I-94 ready (available at the CBP website).',
        documentOptions: [
            {
                id: 'foreign_passport_i94',
                label: 'Required (if Visa/Extension was issued abroad OR you have traveled since your last US-based extension)',
                documentForID: "US State-issued Driver's License / ID OR Foreign Passport",
                documentForAuth: 'Latest electronic I-94 Arrival/Departure Record.',
            },
            {
                id: 'foreign_passport_i797a',
                label: 'Required (if Visa/Extension was issued in US and you have NOT traveled abroad since issuance)',
                documentForID: "US State-issued Driver's License / ID OR Foreign Passport",
                documentForAuth: 'Your I-797A (Approval Notice) with the attached I-94 section.',
            },
        ],
    },
]

const WelcomeAndStatusSelection = forwardRef(({ data, refToPrint, handleNext }, ref) => {

    const [selectedUserType, setSelectedUserType] = useState(data?.userType ?? null)
    const [selectedDocumentOption, setSelectedDocumentOption] = useState(data?.documentOption ?? null)
    const [error, setError] = useState('')

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            if (!selectedUserType) {
                setError('Please select your citizenship or immigration status to proceed')
                return {
                    isValid: false,
                    value: null,
                }
            }

            if (!selectedDocumentOption) {
                setError('Please select your document type to proceed')
                return {
                    isValid: false,
                    value: null,
                }
            }

            setError('')
            return {
                isValid: true,
                value: {
                    userType: selectedUserType,
                    documentOption: selectedDocumentOption,
                },
            }
        },
    }))

    return (
        <div ref={refToPrint} className="flex items-center justify-center px-4 py-10">
            <div className="max-w-3xl w-full bg-white shadow-xl rounded-2xl p-8 space-y-6">

                <div className="flex justify-center">
                    <img src="/trucheck.png" alt="TruCheck" className="h-24 sm:h-[130px] w-auto object-contain" />
                </div>

                <div className="space-y-2">
                    <h1 className="text-2xl font-bold text-gray-800">Identity Verification: Welcome</h1>
                    <p className="text-gray-700 text-base">
                        <strong>We are</strong> committed to ensuring that your profile remains <strong>secure and that your professional identity is protected</strong>. This TruCheck security check creates a foundation of trust for your candidacy and <strong>streamlines</strong> your path to <strong>consideration for this opportunity</strong>.
                    </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                    <p className="font-semibold text-gray-800">General Requirements:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                        <li><strong>A Well-Lit Area</strong> for a quick facial match selfie.</li>
                        <li><strong>Your Identifier:</strong> Last 4 digits of your SSN (US).</li>
                    </ul>
                </div>

                <div className="space-y-3">
                    <p className="text-base text-gray-800 font-medium">
                        Select your status to see required documents and have them ready before proceeding:
                    </p>

                    {USER_TYPES.map((userType) => {
                        const isSelected = selectedUserType === userType.id
                        return (
                            <div
                                key={userType.id}
                                className={`border rounded-xl transition-all ${isSelected
                                    ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-300'
                                    : 'border-gray-200'
                                    }`}
                            >
                                <button
                                    onClick={() => {
                                        setSelectedUserType(userType.id)
                                        setSelectedDocumentOption(
                                            userType.documentOptions.length === 1
                                                ? userType.documentOptions[0].id
                                                : null
                                        )
                                        setError('')
                                    }}
                                    className="w-full text-left px-5 py-4 focus:outline-none"
                                >
                                    <div className="flex items-center justify-between">
                                        <p className={`font-semibold text-base ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}>
                                            {userType.label}
                                        </p>
                                        <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 ml-4 flex items-center justify-center ${isSelected
                                            ? 'border-blue-600 bg-blue-600'
                                            : 'border-gray-300'
                                            }`}>
                                            {isSelected && (
                                                <div className="w-2 h-2 rounded-full bg-white" />
                                            )}
                                        </div>
                                    </div>
                                </button>

                                {isSelected && (
                                    <div className="px-5 pb-4 space-y-2">
                                        {userType.note && (
                                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                                {userType.note}
                                            </p>
                                        )}
                                        {userType.documentOptions.map((option) => {
                                            if (option.subOptions) {
                                                const isGroupSelected = option.subOptions.some(
                                                    (sub) => sub.id === selectedDocumentOption
                                                )
                                                return (
                                                    <div
                                                        key={option.id}
                                                        className={`w-full border rounded-lg px-4 py-3 transition-all ${isGroupSelected
                                                            ? 'border-blue-400 bg-white'
                                                            : 'border-gray-200 bg-white'
                                                            }`}
                                                    >
                                                        {option.condition && (
                                                            <p className="text-xs text-gray-500 mb-0.5">{option.condition}</p>
                                                        )}
                                                        <p className={`text-sm mb-2 ${isGroupSelected ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
                                                            {option.label}
                                                        </p>
                                                        {option.documentForID && (
                                                            <div className="mb-2 bg-blue-50 rounded-lg px-3 py-2 text-xs text-blue-800">
                                                                <span className="font-semibold">Document for ID:</span> {option.documentForID}
                                                            </div>
                                                        )}
                                                        <p className="text-xs text-gray-500 mb-1">Document for Employment Authorization — one of the following:</p>
                                                        <div className="space-y-1 pl-1">
                                                            {option.subOptions.map((subOption) => {
                                                                const isSubSelected = selectedDocumentOption === subOption.id
                                                                return (
                                                                    <button
                                                                        key={subOption.id}
                                                                        onClick={() => {
                                                                            setSelectedDocumentOption(subOption.id)
                                                                            setError('')
                                                                        }}
                                                                        className="w-full text-left flex items-center gap-2 py-1 rounded hover:bg-gray-50 focus:outline-none"
                                                                    >
                                                                        <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${isSubSelected
                                                                            ? 'border-blue-500 bg-blue-500'
                                                                            : 'border-gray-300'
                                                                            }`}>
                                                                            {isSubSelected && (
                                                                                <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                                                            )}
                                                                        </div>
                                                                        <span className={`text-sm ${isSubSelected ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
                                                                            {subOption.label}
                                                                            {subOption.ssnTooltip && (
                                                                                <span className="relative inline-block group ml-1 align-middle">
                                                                                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-xs font-bold cursor-pointer hover:bg-blue-100 hover:text-blue-700 transition-colors">
                                                                                        i
                                                                                    </span>
                                                                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 bg-gray-800 text-white text-xs rounded-lg p-3 hidden group-hover:block z-10 shadow-lg leading-relaxed pointer-events-none">
                                                                                        {SSN_TOOLTIP}
                                                                                        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                                                                                    </span>
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    </button>
                                                                )
                                                            })}
                                                        </div>
                                                    </div>
                                                )
                                            }

                                            const isOptionSelected = selectedDocumentOption === option.id
                                            return (
                                                <button
                                                    key={option.id}
                                                    onClick={() => {
                                                        setSelectedDocumentOption(option.id)
                                                        setError('')
                                                    }}
                                                    className={`w-full text-left border rounded-lg px-4 py-3 transition-all focus:outline-none ${isOptionSelected
                                                        ? 'border-blue-400 bg-white'
                                                        : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${isOptionSelected
                                                            ? 'border-blue-500 bg-blue-500'
                                                            : 'border-gray-300'
                                                            }`}>
                                                            {isOptionSelected && (
                                                                <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                                            )}
                                                        </div>
                                                        <div className="flex-1">
                                                            {option.condition && (
                                                                <p className="text-xs text-gray-500 mb-0.5">{option.condition}</p>
                                                            )}
                                                            <p className={`text-sm ${isOptionSelected ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
                                                                {option.label}
                                                            </p>
                                                            {isOptionSelected && (option.documentForBoth || option.documentForID || option.documentForAuth) && (
                                                                <div className="mt-2 space-y-1 bg-blue-50 rounded-lg px-3 py-2 text-xs text-blue-800">
                                                                    {option.documentForBoth && (
                                                                        <p><span className="font-semibold">Document for both ID and Employment Authorization:</span> {option.documentForBoth}</p>
                                                                    )}
                                                                    {!option.documentForBoth && option.documentForID && (
                                                                        <p><span className="font-semibold">Document for ID:</span> {option.documentForID}</p>
                                                                    )}
                                                                    {!option.documentForBoth && option.documentForAuth && (
                                                                        <p><span className="font-semibold">Document for Employment Authorization:</span> {option.documentForAuth}</p>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {option.note && (
                                                                <p className="text-xs text-amber-700 mt-1">{option.note}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </button>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>

                {error && (
                    <p className="text-sm text-red-600">{error}</p>
                )}

                <div className="text-center">
                    <button
                        onClick={handleNext}
                        className="mt-4 px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg shadow-md transition-all"
                    >
                        Continue to Disclosures
                    </button>
                </div>

            </div>
        </div>
    )

})

export default WelcomeAndStatusSelection
